import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { useTranslation } from 'react-i18next';
import type { CrudFormProps } from '../../components/CrudPage';
import { CrudPage } from '../../components/CrudPage';
import { FormDialog } from '../../components/FormDialog';
import { ResourceControllerService } from '../../api/services/ResourceControllerService';
import type { ResourceDto } from '../../api/models/ResourceDto';
import { generateTempUUID } from '../../utils/uuid';
import type { UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import type { TFunction } from 'i18next';

export type ExcelExportConfigFormResource = {
  id: string;
  resourceId: string;
  resourceName?: string;
  columnOrder: number;
};

export type ExcelExportConfigFormValues = {
  id: string;
  title: string;
  sheetOrder: number;
  resources: ExcelExportConfigFormResource[];
};

type ExcelExportConfigFormFieldsProps = {
  value: ExcelExportConfigFormValues | null;
  availableResources: ResourceDto[];
  resourceOptions: { value: string; text: string }[];
  resourceRows: ExcelExportConfigFormResource[];
  resourcesRefreshKey: number;
  selectedResourceId: string | null;
  setSelectedResourceId: React.Dispatch<React.SetStateAction<string | null>>;
  setResourceRows: React.Dispatch<React.SetStateAction<ExcelExportConfigFormResource[]>>;
  setResourcesRefreshKey: React.Dispatch<React.SetStateAction<number>>;
  register: UseFormRegister<ExcelExportConfigFormValues>;
  watch: UseFormWatch<ExcelExportConfigFormValues>;
  setValue: UseFormSetValue<ExcelExportConfigFormValues>;
  t: TFunction;
};

const ExcelExportConfigFormFields: React.FC<ExcelExportConfigFormFieldsProps> = ({
  availableResources,
  resourceOptions,
  resourceRows,
  resourcesRefreshKey,
  selectedResourceId,
  setSelectedResourceId,
  setResourceRows,
  setResourcesRefreshKey,
  register,
  watch,
  setValue,
  t
}) => {
  const formValues = watch();
  const resources = formValues.resources || [];

  const selectedResourceOptions = resourceOptions.filter(
    (option) => !resources.some((resource: ExcelExportConfigFormResource) => resource.resourceId === option.value)
  );

  const addResource = () => {
    if (!selectedResourceId) {
      return;
    }
    const selected = availableResources.find((resource) => resource.uuid === selectedResourceId);
    if (!selected || !selected.uuid) {
      return;
    }
    const maxOrder = resourceRows.reduce((max, res) => Math.max(max, res.columnOrder || 0), 0);
    const newResource: ExcelExportConfigFormResource = {
      id: generateTempUUID(),
      resourceId: selected.uuid,
      resourceName: selected.name || selected.uuid,
      columnOrder: maxOrder + 1
    };
    const updated = [...resourceRows, newResource];
    setResourceRows(updated);
    setValue('resources', updated);
    setResourcesRefreshKey(prev => prev + 1);
    setSelectedResourceId(null);
  };

  const handleResourcesPriorityChange = useCallback(async (reorderedResources: ExcelExportConfigFormResource[]) => {
    const updated = reorderedResources.map((resource, index) => ({
      ...resource,
      columnOrder: index + 1
    }));
    setResourceRows(updated);
    setValue('resources', updated);
    setResourcesRefreshKey(prev => prev + 1);
  }, [setResourceRows, setResourcesRefreshKey, setValue]);

  const handleResourcesDelete = useCallback((selectedItems: ExcelExportConfigFormResource[]) => {
    if (selectedItems.length === 0) return;
    const idsToDelete = new Set(selectedItems.map((item) => item.id || item.resourceId));
    const remaining = resourceRows.filter((resource) => !idsToDelete.has(resource.id || resource.resourceId));
    const resequenced = remaining.map((resource, index) => ({
      ...resource,
      columnOrder: index + 1
    }));
    setResourceRows(resequenced);
    setValue('resources', resequenced);
    setResourcesRefreshKey(prev => prev + 1);
  }, [resourceRows, setResourceRows, setResourcesRefreshKey, setValue]);

  const sortedResourceRows = [...resourceRows]
    .sort((a, b) => (a.columnOrder || 0) - (b.columnOrder || 0))
    .map((resource) => ({
      ...resource,
      id: resource.id || generateTempUUID()
    }));

  return (
    <>
      <div className="grid grid-cols-1 gap-4 mb-4">
        <TextBoxComponent
          {...register('title')}
          placeholder={`${t('excelExportConfig.titleLabel')} *`}
          floatLabelType="Auto"
        />
      </div>

      <div className="mb-4">
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <DropDownListComponent
              dataSource={selectedResourceOptions}
              fields={{ text: 'text', value: 'value' }}
              value={selectedResourceId || ''}
              change={(e) => setSelectedResourceId(e.value)}
              placeholder={t('excelExportConfig.resourceSelect')}
              floatLabelType="Auto"
            />
          </div>
          <ButtonComponent
            cssClass="e-primary"
            onClick={addResource}
            disabled={!selectedResourceId}
            type="button"
          >
            {t('excelExportConfig.addResource')}
          </ButtonComponent>
        </div>
      </div>

      <div className="mb-4">
        <CrudPage<ExcelExportConfigFormResource>
          key={`excel-export-resources-${resourcesRefreshKey}`}
          titleKey="excelExportConfig.resources"
          showTitle={false}
          gridHeight="250px"
          localMode={true}
          gridMeta={[
            {
              field: 'resourceName',
              headerKey: 'excelExportConfig.resource',
              width: 240,
              render: (data) => data.resourceName || data.resourceId
            }
          ]}
          fetchData={() => Promise.resolve(sortedResourceRows)}
          saveData={async () => undefined}
          deleteData={async () => undefined}
          FormComponent={() => null}
          showDefaultToolbar={true}
          showAddButton={false}
          showEditButton={false}
          showCloneButton={false}
          showDeleteButton={true}
          showSearchButton={false}
          allowFiltering={false}
          allowSorting={false}
          showSummary={false}
          hasPriority={true}
          priorityField="columnOrder"
          onPriorityChange={handleResourcesPriorityChange}
          onDelete={handleResourcesDelete}
        />
        {resourceRows.length === 0 && (
          <div className="p-3 text-sm text-gray-500 border border-gray-200 rounded-b">
            {t('excelExportConfig.noResources')}
          </div>
        )}
      </div>
    </>
  );
};

const ExcelExportConfigFormDialog: React.FC<CrudFormProps<ExcelExportConfigFormValues>> = ({
  value,
  onClose,
  onSave
}) => {
  const { t } = useTranslation(['common']);
  const [availableResources, setAvailableResources] = useState<ResourceDto[]>([]);
  const [selectedResourceId, setSelectedResourceId] = useState<string | null>(null);
  const [resourceRows, setResourceRows] = useState<ExcelExportConfigFormResource[]>([]);
  const [resourcesRefreshKey, setResourcesRefreshKey] = useState(0);

  useEffect(() => {
    const initialResources = (value?.resources ?? []).map((resource, index) => ({
      id: resource.id || resource.resourceId || generateTempUUID(),
      resourceId: resource.resourceId,
      resourceName: resource.resourceName,
      columnOrder: resource.columnOrder || index + 1
    }));
    setResourceRows(initialResources);
    setResourcesRefreshKey(prev => prev + 1);
  }, [value?.resources]);

  useEffect(() => {
    ResourceControllerService.findAll()
      .then(setAvailableResources)
      .catch(() => setAvailableResources([]));
  }, []);

  const resourceOptions = useMemo(
    () => availableResources.map((resource) => ({
      value: resource.uuid || '',
      text: resource.name || resource.uuid || ''
    })),
    [availableResources]
  );

  return (
    <FormDialog
      value={value}
      onClose={onClose}
      onSave={onSave}
      titleKey="excelExportConfig.title"
      width="900px"
    >
      {({ register, watch, setValue }) => (
        <ExcelExportConfigFormFields
          value={value}
          availableResources={availableResources}
          resourceOptions={resourceOptions}
          resourceRows={resourceRows}
          resourcesRefreshKey={resourcesRefreshKey}
          selectedResourceId={selectedResourceId}
          setSelectedResourceId={setSelectedResourceId}
          setResourceRows={setResourceRows}
          setResourcesRefreshKey={setResourcesRefreshKey}
          register={register}
          watch={watch}
          setValue={setValue}
          t={t}
        />
      )}
    </FormDialog>
  );
};

export default ExcelExportConfigFormDialog;
